// redux/services/helpdeskService/helpdesk.Services.js
import { baseApi } from "../baseApi";

export const HelpDeskApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ======================
    // HELP DESK APIs (User)
    // ======================

    // CREATE TICKET
    createTicket: builder.mutation({
      query: (data) => ({
        url: "/ticket-create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["HelpDesk"],
    }),

    // GET TICKETS BY MOBILE NUMBER
    getTicketsByMobile: builder.query({
      query: (mobile) => `/tickets-mobile/${mobile}`,
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ id }) => ({ type: "HelpDesk", id })),
            { type: "HelpDesk", id: "LIST" },
          ]
          : [{ type: "HelpDesk", id: "LIST" }],
    }),

    // GET TICKET BY TICKET_ID
    getTicketByCode: builder.query({
      query: (ticket_id) => `/ticket-code/${ticket_id}`,
      providesTags: (result, error, ticket_id) => [{ type: "HelpDesk", ticket_id }],
    }),

    // ======================
    // HELP DESK APIs (Admin)
    // ======================

    // GET ALL TICKETS (Admin)
    getAdminTickets: builder.query({
      query: ({
        page = 1,
        limit = 10,
        status = "",
        issue_type = "",
        search = "",
      } = {}) =>
        `/tickets?page=${page}&limit=${limit}&status=${status}&issue_type=${issue_type}&search=${search}`,
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ id }) => ({ type: "HelpDesk", id })),
            { type: "HelpDesk", id: "LIST" },
          ]
          : [{ type: "HelpDesk", id: "LIST" }],
    }),

    // GET TICKET BY ID (Admin)
    getAdminTicket: builder.query({
      query: (id) => `/ticket/${id}`,
      providesTags: (result, error, id) => [{ type: "HelpDesk", id }],
    }),

    // GET TICKET STATISTICS (Admin)
    getAdminTicketStats: builder.query({
      query: () => "/tickets-stats",
      providesTags: ["HelpDesk"],
    }),

    // GET ISSUE TYPES (Admin)
    getAdminIssueTypes: builder.query({
      query: () => "/tickets-issue-types",
      providesTags: ["HelpDesk"],
    }),

    // UPDATE TICKET STATUS (Admin)
    updateAdminTicketStatus: builder.mutation({
      query: ({ id, status, assigned_to }) => ({
        url: `/ticket-status/${id}`,
        method: "PUT",
        body: { status, assigned_to },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "HelpDesk", id },
        { type: "HelpDesk", id: "LIST" },
      ],
    }),

    // ADD ADMIN REPLY (Admin)
    addAdminReply: builder.mutation({
      query: ({ id, admin_reply }) => ({
        url: `/ticket-reply/${id}`,
        method: "PUT",
        body: { admin_reply },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "HelpDesk", id },
        { type: "HelpDesk", id: "LIST" },
      ],
    }),

    // RESOLVE TICKET (Admin)
    resolveAdminTicket: builder.mutation({
      query: ({ id, resolution_note, resolved_by }) => ({
        url: `/ticket-resolve/${id}`,
        method: "PUT",
        body: { resolution_note, resolved_by },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "HelpDesk", id },
        { type: "HelpDesk", id: "LIST" },
      ],
    }),

    // DELETE TICKET (Admin)
    deleteAdminTicket: builder.mutation({
      query: (id) => ({
        url: `/ticket-delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "HelpDesk", id },
        { type: "HelpDesk", id: "LIST" },
      ],
    }),
  }),
});

export const {
  // User Hooks
  useCreateTicketMutation,
  useGetTicketsByMobileQuery,
  useGetTicketByCodeQuery,

  // Admin Hooks (Queries)
  useGetAdminTicketsQuery,
  useGetAdminTicketQuery,
  useGetAdminTicketStatsQuery,
  useGetAdminIssueTypesQuery,

  // Admin Hooks (Mutations)
  useUpdateAdminTicketStatusMutation,
  useAddAdminReplyMutation,
  useResolveAdminTicketMutation,
  useDeleteAdminTicketMutation,
} = HelpDeskApi;

export default HelpDeskApi;